import React from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SupportChat } from "@/components/SupportChat";
import { FeedbackCampaign } from "@/components/FeedbackCampaign";
import { InstallPrompt } from "@/components/InstallPrompt";
import { Phone, Globe, History, Wallet, LayoutDashboard, LogOut, Settings as SettingsIcon, Smartphone, Send, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const baseNavItems = [
  { to: "/app", label: "Keypad", icon: Phone, testid: "nav-dialer" },
  { to: "/rates", label: "Rates", icon: Globe, testid: "nav-rates" },
  { to: "/history", label: "Recents", icon: History, testid: "nav-history" },
  { to: "/wallet", label: "Credit", icon: Wallet, testid: "nav-wallet" },
  { to: "/esim", label: "eSIM", icon: Smartphone, testid: "nav-esim" },
];
const sendNavItem = { to: "/send", label: "Send", icon: Send, testid: "nav-send" };

export default function Layout({ children }) {
  const location = useLocation();
  const onDialer = location.pathname === "/app" || location.pathname === "/connect";
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sendEnabled, setSendEnabled] = useState(false);

  useEffect(() => {
    api.get("/remit/config")
      .then(({ data }) => setSendEnabled(data.enabled || user?.role === "admin"))
      .catch(() => {});
  }, [user]);

  const navItems = sendEnabled
    ? [...baseNavItems.slice(0, 4), sendNavItem, baseNavItems[4]]
    : baseNavItems;

  const handleLogout = () => {
    navigate("/");
    setTimeout(logout, 150);
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="sticky top-0 z-40 bg-white border-b border-black/10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <NavLink to="/app" className="flex items-center" data-testid="brand-logo">
            <img src="/branding/zanitel_logo.png" alt="Zeeptel — connecting the world" className="h-9 w-auto" />
          </NavLink>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-2.5 xl:px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    isActive ? "bg-accent text-accent-foreground" : "text-zinc-600 hover:bg-zinc-100"
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-2.5 xl:px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    isActive ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
                  }`
                }
              >
                <LayoutDashboard className="h-4 w-4" />
                Admin
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <NavLink
              to="/wallet"
              data-testid="load-airtime-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#E11D48] text-white text-xs font-bold hover:bg-[#BE123C] transition-colors whitespace-nowrap"
              title="Add credit to your wallet"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Load Airtime</span>
            </NavLink>
            <NavLink
              to="/wallet"
              className="flex flex-col items-end leading-tight px-3 py-1.5 rounded-xl bg-accent hover:bg-emerald-100 transition-colors"
              data-testid="balance-pill"
              title="View wallet"
            >
              <span className="text-[10px] uppercase tracking-wide font-bold text-accent-foreground/70">Balance</span>
              <span className="font-mono font-bold text-accent-foreground">${(user?.balance ?? 0).toFixed(2)}</span>
            </NavLink>
            <NavLink
              to="/settings"
              data-testid="nav-settings"
              className="h-9 w-9 flex items-center justify-center rounded-xl border border-black/10 hover:bg-zinc-100 transition-colors"
              title="Settings"
            >
              <SettingsIcon className="h-4 w-4" />
            </NavLink>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="h-9 w-9 flex items-center justify-center rounded-xl border border-black/10 hover:bg-zinc-100 transition-colors"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl xl:max-w-7xl w-full mx-auto px-4 md:px-8 pt-6 md:pt-10 pb-24 md:pb-10 relative">
        <div className="hidden lg:block fixed left-0 top-16 bottom-0 w-[430px] pointer-events-none select-none z-0" aria-hidden="true" data-testid="app-bg-image">
          <img
            src={location.pathname.startsWith("/send") ? "/images/send-money-bg.jpg" : "/images/dialer-caller.jpg"}
            alt=""
            className="h-full w-full object-cover object-top opacity-[0.34] lg:opacity-[0.72]"
            style={{
              filter: "contrast(1.08) saturate(1.08)",
              transform: "translateX(-110px)",
              WebkitMaskImage: "linear-gradient(to right, black 55%, transparent 99%)",
              maskImage: "linear-gradient(to right, black 55%, transparent 99%)",
            }}
          />
        </div>
        <div className="relative z-10">{children ?? <Outlet />}</div>
      </main>

      {/* Mobile bottom nav — native app style */}
      <nav className={`md:hidden sticky bottom-0 z-40 backdrop-blur-xl bg-white/90 backdrop-saturate-150 border-t border-slate-200 grid ${navItems.length === 6 ? "grid-cols-6" : "grid-cols-5"} pb-[max(0.4rem,env(safe-area-inset-bottom))] pt-1.5`}>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`${item.testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-1.5 gap-1 text-[11px] font-semibold transition-colors ${
                isActive ? "text-primary" : "text-slate-400"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={`flex items-center justify-center h-8 w-14 rounded-full transition-colors ${isActive ? "bg-blue-50" : ""}`}>
                  <item.icon className="h-5 w-5" strokeWidth={isActive ? 2.4 : 2} />
                </span>
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <InstallPrompt hideLauncher={onDialer} />
      <SupportChat raised hideLauncher={onDialer} />
      <FeedbackCampaign raised hideLauncher={onDialer} />
    </div>
  );
}
